import * as React from 'react';
import Dropzone from 'react-dropzone';
import UploadIcon from '@mui/icons-material/CloudUpload';
import stripLabel from './util/stripLabel';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { BigDropZone } from './util/BigDropZone';
import { Container } from './util/Container';
import { ExtractionContext } from '../../context/ExtractionContext';
import { ITrainingSet } from '../../types/my-types';
import { UploadContext } from './UploadContext';
import { extractFileData } from './util/extractFileData';
import { finishUpload } from './util/finishUpload';
import { makeFilesIterator } from './util/makeFilesIterator';
import { splitInChunks } from './util/splitInChunks';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UploadZoneProps {
  type?: string;
  trainingSet?: ITrainingSet;
  trainingSetError?: boolean;
  isExtraction: boolean;
  isUpdate?: boolean;
  dismissModal: () => void;
  setOption: (value: number) => void;
}

export interface IUploadZoneState {
  documents: any[];
}

const UploadZone: React.FC<UploadZoneProps> = ({
  trainingSetError,
  trainingSet,
  type,
  isExtraction,
  isUpdate,
  dismissModal,
  setOption,
}) => {
  const MAX_FILES = 1500;

  const { setToast, dataPoolId } = React.useContext(AppContext);
  const {
    label,
    setUploading,
    updateTotalUploadedDocs,
    updateTotalAcceptedDocs,
    updateTotalFailedDocs,
    acceptedDocs,
    setAcceptedDocs,
    isExperimental,
  } = React.useContext(UploadContext);

  const glynt = useGlynt();
  const cache = useQueryClient();
  const navigate = useNavigate();

  // it's using the legacy method upload, which scales better than the new method
  const { mutateAsync: createDoc } = useMutation({
    mutationFn: async (data: any) => {
      let { label, content_type, content_md5 } = data;
      return await (await glynt).document.create({ label, content_md5, content_type });
    },

    retry: 3,
    retryDelay: 1000,

    onSuccess: async (doc, { file }) => {
      // if md5 mismatch, it will fail to upload
      await uploadToAWS(doc, file);
      updateTotalUploadedDocs((prev) => prev + 1);
      let docs = acceptedDocs;
      docs.push(doc);
      // console.log({ doc, file, docs });
      setAcceptedDocs(docs);
    }
  });
  const { mutateAsync: createExtractionBatch } = useMutation({
    mutationFn: async (data: any) => {
      return await (await glynt).extractionBatch.create(data);
    }
  });
  const { mutateAsync: createTrainingSet } = useMutation({
    mutationFn: async (data: any) => {
      return await (await glynt).trainingSet.create(data);
    },

    onError: (error) => {
      setToast({
        open: true,
        variant: 'error',
        message: `Errrr ${JSON.stringify(error)}`,
      });
    }
  });
  const { mutateAsync: generatePages } = useMutation({
    mutationFn: async (param: { id: string; extraction: boolean }) => {
      const client = await glynt;
      await (param.extraction ? client.extractionBatch : client.trainingSet).generatePages(
        param.id
      );
    }
  });
  const { mutateAsync: updateTrainingSet } = useMutation({
    mutationFn: async (params: any) => {
      return await (await glynt).trainingSet.update(params.id, params.data);
    }
  });

  const uploadToAWS = (document: any, file: any) => {
    return new Promise((resolve, reject) => {
      fetch(document.file_upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': document.content_type,
          'Content-MD5': document.content_md5,
        },
        body: file.content as ArrayBuffer,
      })
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          updateTotalFailedDocs((prev) => prev + 1);
          reject(error);
        });
    });
  };

  const uploadChunk = async (chunk: any[]) => {
    const filesReady = chunk.map(async (currentFile: any) => {
      let file: {
        label: any;
        type: any;
        md5Content: any;
      } | null = await extractFileData(currentFile);

      try {
        await createDoc({
          label: stripLabel(file.label),
          content_type: file.type,
          content_md5: file.md5Content,
          file: file,
        });

        file = null;
      } catch {
        // console.log(error);
        updateTotalFailedDocs((prev) => prev + 1);
      }
    });

    return Promise.all(filesReady)
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  };

  return (
    <div className="dropzone" data-cy="dropzone-wrapper">
      <Dropzone
        disabled={trainingSetError || label === ''}
        maxFiles={MAX_FILES}
        accept={{ 'application/pdf': [], 'image/tiff': [], 'image/jpeg': [], 'image/png': [] }}
        onDrop={async (accepted) => {
          if (accepted.length > 0 && accepted.length <= MAX_FILES) {
            setOption(2);
            updateTotalAcceptedDocs(accepted.length);
            // console.log('accepted: ', accepted.length);
            setUploading(true);

            // TODO: double check is it a reason to upload file one-by-one?
            const chunks = accepted.reduce(splitInChunks(), []);

            const it = makeFilesIterator(chunks);

            let result = it.next();
            while (!result.done) {
              const currentChunk = result.value;

              await uploadChunk(currentChunk);

              result = it.next();
            }

            const retData = await finishUpload(
              isExtraction,
              createExtractionBatch,
              dataPoolId,
              acceptedDocs,
              trainingSet,
              label,
              setToast,
              isUpdate,
              updateTrainingSet,
              createTrainingSet,
              generatePages,
              history,
              cache,
              isExperimental
            );
            dismissModal();

            if (retData?.returnedType === 'trainingSet' && retData?.returnedId) {
              navigate(`/data-pools/${dataPoolId}/train/${retData.returnedId}/details`);
            }
          } else {
            setToast({
              open: true,
              variant: 'error',
              message: `Too Large: Maximum Batch Size is ${MAX_FILES} Documents`,
            });
          }
        }}
      >
        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => {
          return (
            <Container
              // @ts-ignore
              isDragActive={isDragActive}
              isDragReject={isDragReject}
              type={type}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <BigDropZone
                // @ts-ignore
                isDragActive={isDragActive}
              >
                <UploadIcon style={{ fontSize: '100px', color: 'cadetblue' }} />
              </BigDropZone>
              <span
                id={isDragActive ? 'upload-zone-message-dragging' : 'upload-zone-message'}
                style={{
                  textAlign: 'center',
                  color: type ? 'white' : '',
                  fontSize: '14px',
                }}
              >
                {type ? 'Browse for Files' : 'Drop to Upload Files'}
              </span>
            </Container>
          );
        }}
      </Dropzone>
    </div>
  );
};

export default UploadZone;
